import { useEffect, useRef, useState } from "react";
import { GOOGLE_CLIENT_ID } from "../config/api";

let nextButtonId = 0;
let initializedSdk;
let activeButtonId;
const callbacks = new Map();

// Google has one page-wide callback. Route the response back to the button
// that initiated sign-in, so the review and contact buttons can coexist.
export function renderGoogleButton(element, onCredential, options = {}) {
  const sdk = window.google.accounts.id;
  const buttonId = `ptn-google-${++nextButtonId}`;
  callbacks.set(buttonId, onCredential);
  if (initializedSdk !== sdk) {
    sdk.initialize({
      client_id: GOOGLE_CLIENT_ID,
      ux_mode: "popup",
      callback: (response) => {
        callbacks.get(response.state || activeButtonId)?.(response);
      },
    });
    initializedSdk = sdk;
  }
  sdk.renderButton(element, {
    theme: "outline",
    size: "large",
    type: "standard",
    shape: "pill",
    width: 300,
    ...options,
    state: buttonId,
    click_listener: () => { activeButtonId = buttonId; },
  });
  return () => {
    callbacks.delete(buttonId);
    if (activeButtonId === buttonId) activeButtonId = undefined;
  };
}

export default function GoogleSignInButton({ onCredential }) {
  const buttonRef = useRef(null);
  const callbackRef = useRef(onCredential);
  const [error, setError] = useState("");
  callbackRef.current = onCredential;

  useEffect(() => {
    let dispose;
    let active = true;
    const initialize = () => {
      if (!active || dispose || !window.google?.accounts?.id) return;
      buttonRef.current.innerHTML = "";
      dispose = renderGoogleButton(buttonRef.current, (response) =>
        callbackRef.current(response)
      );
    };
    const handleError = () => {
      if (active) setError("Google sign-in could not load. Please refresh and try again.");
    };

    let script = document.getElementById("google-gsi-script");
    if (!script) {
      script = document.createElement("script");
      script.id = "google-gsi-script";
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", initialize);
    script.addEventListener("error", handleError);
    initialize();
    return () => {
      active = false;
      dispose?.();
      script.removeEventListener("load", initialize);
      script.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <div>
      <div ref={buttonRef} className="inline-flex justify-center" />
      {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
